<template>
  <a-button
    class="button payment-button"
    type="primary"
    @click="handleOpenNiubiz"
    :disabled="disabled"
    :loading="loading"
  >
    {{ props.name }}
  </a-button>

  <a-modal
    v-model:open="cardFormOpen"
    :title="title"
    centered
    :bodyStyle="{ padding: '20px 32px' }"
    :width="640"
    :footer="null"
    @cancel="handleClose"
  >
    <a-spin :spinning="loading">
      <div class="details">
        <h2 class="title">{{ detailsTitle }}</h2>

        <p class="description">
          Finaliza tu pago de forma segura con nuestro sistema de pago protegido. Garantizamos la
          privacidad y seguridad de tus datos en cada transacción.
        </p>

        <img :src="acceptedCardsImage" alt="accepted-cards" />
      </div>

      <a-form layout="vertical" :model="formState">
        <div class="half-split">
          <a-form-item label="Nombres" required>
            <a-input v-model:value="formState.name" placeholder="Escribe aquí..." />
          </a-form-item>

          <a-form-item label="Apellidos" required>
            <a-input v-model:value="formState.lastname" placeholder="Escribe aquí..." />
          </a-form-item>
        </div>

        <section class="payment-form">
          <a-form-item label="Número de tarjeta" required>
            <div id="txtNumeroTarjeta" class="payment-input" />
          </a-form-item>

          <div class="half-split">
            <a-form-item label="Fecha de vencimiento" required>
              <div id="txtFechaVencimiento" class="payment-input" />
            </a-form-item>

            <a-form-item label="Código de seguridad (CVV)" required>
              <div id="txtCvv" class="payment-input" />
            </a-form-item>
          </div>

          <a-form-item v-if="cardInstallments" label="Número de cuotas" required>
            <a-select
              :getPopupContainer="(triggerNode) => triggerNode.parentNode"
              v-model:value="formState.installment"
              placeholder="Cuotas"
            >
              <a-select-option v-for="inst in cardInstallments" :key="inst" :value="inst">
                {{ inst }}
              </a-select-option>
            </a-select>
          </a-form-item>

          <div class="termns">
            <a-checkbox v-model:checked="formState.termns">
              <span class="required">*</span>
              Acepto los
              <a
                href="https://securitec.pe/politicas-privacidad/"
                target="_blank"
                rel="noopener noreferrer"
                >términos y condiciones</a
              >
            </a-checkbox>
          </div>

          <a-divider type="horizontal" class="divider" />

          <div v-if="showAmountInput" class="summary summary--input">
            <span>Total a recargar:</span>
            <a-input-number
              v-model:value="paymentAmount"
              :min="minAmount"
              :precision="2"
              addon-before="$"
            />
          </div>
          <div v-else class="summary">
            <span>Total</span>
            <span>${{ fixedAmount }}</span>
          </div>

          <a-tooltip :title="isDisabled ? 'Completa todos los campos' : null">
            <a-button
              class="button"
              type="primary"
              :disabled="isDisabled"
              @click="handlePay"
              :loading="paying"
            >
              {{textButton}}
            </a-button>
          </a-tooltip>

          <a-button class="button" @click="handleClose">Cancelar</a-button>

          <p class="info">No se almacenará ningún dato de la tarjeta registrada</p>
        </section>
      </a-form>
    </a-spin>
  </a-modal>
</template>

<script setup>
import { useRouter } from "vue-router";
import { ref, nextTick, computed, reactive, watch } from "vue";
import { message } from "ant-design-vue";
import { useStore } from "vuex";
import EventBus from "@/app/shared/utils/eventBus";
import SlotManager from "@/app/settings/services/slots/SlotManager";
import { integrationManager } from "@/app/integrations/services/IntegrationManager";

// NECESARIOS PARA LA RECARGA DE FACTURA VENCIDA
import items from "@/app/common/components/organisms/TheSidebar/items";
import { fetchDataUser } from "@/app/auth/services";
import { EventManagement, StateOptions } from "@/app/shared/utils/enums";
import { registerEventManagement } from "@/app/shared/services";
// -----FIN

import { paymentServices } from "@/app/shared/services/PaymentServices";
import { ProductType } from "@/@types/settings/billing";
import acceptedCardsImage from "@/app/settings/assets/accepted-cards.png";
import { safeRegisterEventManagement } from "../../composables/useRegisterEventManagement";

const props = defineProps({
  amount: { type: Number, required: true },
  currency: { type: String, default: "USD" },
  name: { type: String, default: "Recargar saldo" },
  type: { type: Number, default: 1 },
  disabled: { type: Boolean, default: false },
  count: { type: Number, default: 0 },
  price: { type: Number, default: 0 },
  apiKey: { type: String },
  title: { type: String, default: "Recarga tu saldo de WhatsApp" },
  isExpiredBill: { type: Boolean, default: false },
  customerPlanConfigToken: { type: String, default: "" },
  textButton: { type: String, default: "Recargar" },
  action: { type: String, default: "recarga" },
  showActionInTitle: { type: Boolean, default: true },
  showAmountInput: { type: Boolean, default: false },
  minAmount: { type: Number, default: 0 },
});

const emit = defineEmits(["update", "success", "reject"]);
const manager = new SlotManager();

const store = useStore();
const userData = computed(() => store.getters["getDataUser"]);
const paymentAmount = ref(props.amount);
const fixedAmount = computed(() => Number(paymentAmount.value || 0).toFixed(2));
const detailsTitle = computed(() =>
  props.showActionInTitle
    ? `Registra tu método de pago y finaliza tu ${props.action}`
    : "Registra tu método de pago"
);
const formState = reactive({
  name: "",
  lastname: "",
  installment: undefined,
  termns: false,
});

const elementStyles = {
  base: {
    color: "#666666",
    fontWeight: 400,
    fontFamily: "'Montserrat', sans-serif",
    fontSize: "16px",
    fontSmoothing: "antialiased",
    placeholder: {
      color: "#bfbfbf",
    },
    autofill: {
      color: "#e39f48",
    },
  },
  invalid: {
    color: "#E25950",
    "::placeholder": {
      color: "#FFCCA5",
    },
  },
};

const sessionData = ref();
const sessionAmount = ref("");
const loading = ref(false);
const cardFormOpen = ref(false);

const cardNumber = ref();
const cardExpiry = ref();
const cardCvc = ref();

const cardInstallments = ref();

const createFormElements = async () => {
  cardNumber.value = await payform.createElement(
    "card-number",
    {
      style: elementStyles,
      placeholder: "xxxx-xxxx-xxxx-xxxx",
    },
    "txtNumeroTarjeta"
  );

  cardNumber.value.on("bin", (data) => console.log("bin", data));
  cardNumber.value.on("change", (data) => console.log("change", data));
  cardNumber.value.on("dcc", (data) => console.log("dcc", data));
  cardNumber.value.on("installments", (data) => {
    console.log("installments", data);
    cardInstallments.value = data;
    formState.installment = data?.[0];
  });
  cardNumber.value.on("lastFourDigits", (data) => console.log("lastFourDigits", data));

  cardExpiry.value = await payform.createElement(
    "card-expiry",
    {
      style: elementStyles,
      placeholder: "mm/aa",
    },
    "txtFechaVencimiento"
  );

  cardCvc.value = await payform.createElement(
    "card-cvc",
    {
      style: elementStyles,
      placeholder: "xxx",
    },
    "txtCvv"
  );
};

/**
 * Resuelve el correo del cliente para enviarlo a Niubiz (customer.email / customerEmail).
 * Cubre, en orden, los flujos donde se usa este botón:
 * 1. Usuario logueado (recargas, slots, IA, TTS, upgrade/downgrade).
 * 2. Registro de nueva cuenta (sin sesión, token temporal validado).
 * 3. Login/MFA reciente o factura vencida (sessionStorage.userEmail).
 * 4. Credenciales guardadas para reintentar login (authenticationData).
 */
const resolveUserEmail = () => {
  const fromLoggedUser = userData.value?.email?.trim();
  if (fromLoggedUser) return fromLoggedUser;

  const fromRegisterFlow = store.getters["getTemporalUserEmail"]?.trim();
  if (fromRegisterFlow) return fromRegisterFlow;

  const fromSession = sessionStorage.getItem("userEmail")?.trim();
  if (fromSession) return fromSession;

  const authData = sessionStorage.getItem("authenticationData");
  if (authData) {
    try {
      const decoded = window.atob(authData);
      const email = decoded.split(":")[0]?.trim();
      if (email) return email;
    } catch (error) {
      console.error("No se pudo decodificar authenticationData", error);
    }
  }

  return "";
};

watch(
  () => props.amount,
  (amount) => {
    paymentAmount.value = amount;
  }
);

const refreshPaymentSession = async () => {
  const storedData = sessionStorage.getItem("authenticationData");

  if (props.type == ProductType.Register && props.customerPlanConfigToken) {
    sessionData.value = await paymentServices.getSessionToken(
      fixedAmount.value,
      props.customerPlanConfigToken
    );
  } else if (storedData) {
    sessionData.value = await paymentServices.getSessionTokenToRecharge(
      fixedAmount.value,
      storedData
    );
  } else {
    sessionData.value = await paymentServices.getSessionToken(fixedAmount.value);
  }

  sessionAmount.value = fixedAmount.value;

  payform.setConfiguration({
    sessionkey: sessionData.value.sessionKey,
    purchasenumber: sessionData.value.purchase_code,
    channel: "web",
    merchantid: import.meta.env.VITE_NIUBIZ_MERCHANT_ID,
    amount: fixedAmount.value,
    language: "es",
    font: "https://fonts.googleapis.com/css?family=Montserrat:400&display=swap",
    recurrencemaxamount: "",
  });
};

const handleOpenNiubiz = async () => {
  if (props.isExpiredBill) {
    if (!resolveUserEmail()) {
      router.push("/login");
      return;
    }
  }

  loading.value = true;

  try {
    const niubizScript = document.createElement("script");
    niubizScript.defer = true;
    niubizScript.setAttribute("src", import.meta.env.VITE_NIUBIZ_SCRIPT_URI);
    // niubizScript.setAttribute(
    //   "src",
    //   "https://static-content.vnforapps.com/elements/v1/payform.min.js"
    // );
    niubizScript.setAttribute("id", "niubizScript");
    document.head.appendChild(niubizScript);

    console.log(props.type, 'props.type');
    await refreshPaymentSession();

    console.log(sessionData.value.purchase_code, 'sessionData.value.purchase_code')

    cardFormOpen.value = true;

    await nextTick();

    await createFormElements();
  } catch (error) {
    console.error(error);
    message.error("Ha ocurrido un error");
  }

  loading.value = false;
};

const purchaseSlots = async () => {
  const response = await manager.buySlots(props.price, props.count);
  console.log(response, "response purcharse");
};

const purchaseIA = async () => {
  const response = await integrationManager.updateCustomerSetting(props.apiKey, true);
  console.log(response, "response purcharse IA");
};
const isDisabled = computed(() => {
  const hasPersonalData = formState.name && formState.lastname;

  const isFormFulfilled = cardInstallments.value
    ? hasPersonalData && formState.installment
    : hasPersonalData;

  return !(isFormFulfilled && formState.termns);
});

const paying = ref(false);

const handlePay = async () => {
  const customerEmail = resolveUserEmail();

  if (!customerEmail) {
    message.error("No se pudo identificar tu correo. Vuelve a iniciar sesión e inténtalo de nuevo.");
    return;
  }

  if (sessionAmount.value !== fixedAmount.value) {
    await refreshPaymentSession();
  }

  const data = {
    name: formState.name,
    lastName: formState.lastname,
    email: customerEmail,
    alias: sessionData.value.purchase_code,
  };

  if (formState.installment) data.installment = formState.installment;

  paying.value = true;

  try {
    const response = await payform.createToken(
      [cardNumber.value, cardExpiry.value, cardCvc.value],
      data
    );

    const { state, access: accessToken } = await paymentServices.processPayment({
      amount: fixedAmount.value,
      purchase_number: sessionData.value.purchase_code,
      purchaseCode: data.alias,
      userData: data,
      tokenData: response,
      productType: props.type,
      customerPlanConfigToken: props.customerPlanConfigToken,
    });

    if (state === "Verified" || state === "Confirmed" || state === "Authorized") {
      if (props.type === ProductType.Slot) {
        purchaseSlots();
        EventBus.emit("updateSlots");
        EventBus.emit("updateTable");
        message.success("Se han comprado slots con éxito");
      }
      if (props.type === ProductType.Recharge) {
        message.success("El pago fue realizado con éxito");
        // FOR RECHARGING SLOTS
        if (accessToken) {
          store.dispatch("setAccessToken", { accessToken, username: customerEmail });
          if (state === "Authorized") goIntoApp();
        }
      }
      if (props.type === ProductType.Summary) {
        purchaseIA();
        EventBus.emit("showSuccessModal");
        message.success("El pago de IA fue realizado con éxito");
      }
      emit("success");
    } else {
      emit("reject");
    }
    handleClose();
    emit("update");
  } catch (error) {
    message.error(error);
    console.error(error);
  }

  paying.value = false;
};

const handleClose = () => {
  const txtNumeroTarjeta = document.querySelector("#txtNumeroTarjeta");
  const txtFechaVencimiento = document.querySelector("#txtFechaVencimiento");
  const txtCvv = document.querySelector("#txtCvv");

  txtNumeroTarjeta.removeChild(txtNumeroTarjeta.firstChild);
  txtFechaVencimiento.removeChild(txtFechaVencimiento.firstChild);
  txtCvv.removeChild(txtCvv.firstChild);

  cardFormOpen.value = false;

  const niubizScript = document.querySelector("#niubizScript");
  niubizScript.remove();
};

const router = useRouter();

const goIntoApp = async () => {
  // Para establecer las opciones del sidebar y redirigir al home
  try {
    const { options_menu } = await fetchDataUser();

    const firstUserMenu = options_menu[0];
    const firstUserSubmenu = firstUserMenu?.items?.[0];

    const menusList = items.flatMap((item) => {
      const menus = [item];

      if (item.subitems) menus.push(...item.subitems);

      return menus;
    });

    const path = menusList.find((item) =>
      firstUserSubmenu ? item.scope === firstUserSubmenu.scope : item.scope === firstUserMenu.scope
    );

    localStorage.removeItem("tabCount");
    await safeRegisterEventManagement(EventManagement.Online, {
      state: StateOptions.Connected,
      module: 'payu-payment-button',
    });
    router.push(path.route);
  } catch (error) {
    console.log(error);
  }
};
</script>

<style lang="sass" scoped>
.details
  display: flex
  flex-direction: column
  gap: 16px
  align-items: center
  justify-content: center
  margin-bottom: 20px
  text-align: center

  .title
    margin: 0
    color: $gray-9
    font-weight: 600
    font-size: 24px

  .description
    margin: 0
    color: $text-secondary

.button
  width: 100%
  margin-bottom: 10px
.half-split
  display: flex
  gap: 16px

  & > *
    width: 100%

.payment-form
  margin-bottom: 0

  .payment-input
    border: 1px solid $gray-5
    padding: 4px 11px
    border-radius: 6px
    min-height: 32px

.divider
  margin: 16px 0 12px
  background-color: $gray-5

.summary
  display: flex
  align-items: center
  justify-content: space-between
  margin-bottom: 16px

  span
    font-weight: 600
    font-size: 20px

  &--input
    justify-content: flex-end
    gap: 12px

.info
  font-size: 12px
  color: $gray-8
  margin: 0

.termns
  display: flex
  position: relative
  gap: 4px

  .required
    color: #ff4d4f
    font-family: SimSun,sans-serif

:global(.payment-form iframe)
    width: 100%

:deep(.ant-tooltip-disabled-compatible-wrapper)
  width: 100%
.disabled
  width: 100%
  color: $gray-7
  padding: 8px
  border-radius: 4px
  border: 2px solid $gray-5
  background: $gray-7
</style>
